import { useEffect, useRef, useState } from "react";
import { Play, Pause, Share2, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import commercialAsset from "@/assets/commercial/geniebot-30s/geniebot-commercial-v5.mp4.asset.json";
import posterAsset from "@/assets/commercial/geniebot-30s/geniebot-commercial-v5-poster.jpg.asset.json";

/**
 * CommercialShowcase — "See GenieBot in 30 Seconds"
 * 9:16 vertical player, lazy-mounted on first intersection, pauses offscreen,
 * respects prefers-reduced-motion & Save-Data. No new analytics vendors.
 */
export const CommercialShowcase = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mounted, setMounted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const firedRef = useRef({ p25: false, p50: false, p75: false, done: false, impression: false });

  const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const saveData = typeof navigator !== "undefined" && (navigator as unknown as { connection?: { saveData?: boolean } }).connection?.saveData === true;
  const autoplayEligible = !reduced && !saveData;

  const track = (event: string, data: Record<string, unknown> = {}) => {
    try {
      window.dispatchEvent(new CustomEvent("geniebot:analytics", { detail: { event, ...data } }));
    } catch { /* noop */ }
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el || mounted) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setMounted(true);
            if (!firedRef.current.impression) {
              firedRef.current.impression = true;
              track("commercial_impression");
            }
          }
        });
      },
      { rootMargin: "300px 0px", threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [mounted]);

  useEffect(() => {
    if (!mounted) return;
    const el = containerRef.current;
    const video = videoRef.current;
    if (!el || !video) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting && !video.paused) video.pause();
          else if (e.isIntersecting && autoplayEligible && video.paused) video.play().catch(() => {});
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [mounted, autoplayEligible]);

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const p = v.currentTime / v.duration;
    if (p >= 0.25 && !firedRef.current.p25) { firedRef.current.p25 = true; track("commercial_progress", { pct: 25 }); }
    if (p >= 0.5 && !firedRef.current.p50) { firedRef.current.p50 = true; track("commercial_progress", { pct: 50 }); }
    if (p >= 0.75 && !firedRef.current.p75) { firedRef.current.p75 = true; track("commercial_progress", { pct: 75 }); }
    if (p >= 0.98 && !firedRef.current.done) { firedRef.current.done = true; track("commercial_complete"); }
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().then(() => track("commercial_play")).catch(() => {});
    else v.pause();
  };

  const share = async () => {
    const shareData = {
      title: "GenieBot.pro — See it in 30 seconds",
      text: "Powerful AI tools in one place. Watch the 30-second commercial.",
      url: "https://geniebot.pro/",
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        track("commercial_share", { method: "web-share" });
        return;
      }
      await navigator.clipboard.writeText(shareData.url);
      track("commercial_share", { method: "clipboard" });
      alert("Link copied to clipboard");
    } catch { /* user cancelled */ }
  };

  return (
    <section aria-labelledby="commercial-heading" className="relative py-20 md:py-28 overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
        {/* Copy */}
        <div className="order-2 md:order-1 space-y-6">
          <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-primary/80">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" aria-hidden />
            30-Second Preview
          </div>
          <h2 id="commercial-heading" className="text-4xl md:text-5xl font-bold leading-tight">
            See GenieBot <span className="text-primary">in 30 seconds</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-lg">
            Talk to it. Ask it about your files. Build your own bot. Do more with AI — without juggling apps.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button asChild size="lg">
              <Link to="/auth" onClick={() => track("commercial_cta_click", { target: "auth" })}>
                Explore GenieBot
              </Link>
            </Button>
            <Button variant="outline" size="lg" onClick={share} aria-label="Share this commercial">
              <Share2 className="mr-2 h-4 w-4" aria-hidden /> Share
            </Button>
          </div>
        </div>

        {/* Video */}
        <div
          ref={containerRef}
          className="order-1 md:order-2 mx-auto w-full max-w-[340px] aspect-[9/16] rounded-3xl overflow-hidden bg-black shadow-2xl ring-1 ring-white/10 relative group"
        >
          {mounted ? (
            <>
              <video
                ref={videoRef}
                className="h-full w-full object-cover"
                src={commercialAsset.url}
                poster={posterAsset.url}
                preload="metadata"
                playsInline
                loop
                muted={muted}
                autoPlay={autoplayEligible}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onTimeUpdate={onTimeUpdate}
                aria-label="GenieBot.pro 30-second commercial. Captioned."
              >
                <track
                  kind="captions"
                  srcLang="en"
                  src="/geniebot-commercial-captions.vtt"
                  label="English"
                  default
                />
                <a href={commercialAsset.url}>Download the GenieBot.pro commercial (MP4)</a>
              </video>
              {/* Play/pause overlay */}
              <button
                type="button"
                onClick={togglePlay}
                aria-label={playing ? "Pause commercial" : "Play commercial"}
                className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/30 focus-ring transition-colors"
              >
                <span
                  className={`h-16 w-16 rounded-full bg-white/95 text-black grid place-items-center shadow-lg transition-opacity ${
                    playing ? "opacity-0 group-hover:opacity-100" : "opacity-100"
                  }`}
                >
                  {playing ? <Pause className="h-7 w-7" /> : <Play className="h-7 w-7 ml-1" />}
                </span>
              </button>
              {/* Mute toggle */}
              <button
                type="button"
                onClick={() => setMuted((m) => !m)}
                aria-label={muted ? "Unmute commercial" : "Mute commercial"}
                className="absolute bottom-3 right-3 h-9 w-9 rounded-full bg-black/60 text-white grid place-items-center hover:bg-black/80 focus-ring"
              >
                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </button>
            </>
          ) : (
            <img
              src={posterAsset.url}
              alt="GenieBot.pro commercial preview poster"
              className="h-full w-full object-cover"
              loading="lazy"
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default CommercialShowcase;
